'use client';
import { useSetAtom } from 'jotai';
import { type PropsWithChildren, useEffect } from 'react';
import styled from 'styled-components';

import { ImageBackground, type ImageBackgroundProps } from '../image-background/ImageBackground';

import { BackgroundViewAtom } from './BackgroundView.atom';
import { MidContent } from './MidContent';
import { LeftActions, RightActions, Title, Top } from './TopContent';

export type BackgroundViewProps = PropsWithChildren<
	Omit<ImageBackgroundProps, 'src'> & {
		isLoading?: boolean;
		src?: string;
		title?: string;
	}
>;

export const BackgroundView = ({
	imageStyle,
	isLoading,
	objectFit,
	src,
	title,
	...backgroundProps
}: BackgroundViewProps) => {
	const setAtom = useSetAtom(BackgroundViewAtom);

	useEffect(() => {
		setAtom({ isLoading, title });
	}, [title, isLoading, setAtom]);

	return isLoading || !src ? (
		<S.BackgroundPlaceholder>{backgroundProps.children}</S.BackgroundPlaceholder>
	) : (
		<ImageBackground
			imageStyle={imageStyle ?? { height: '100%', width: '100%' }}
			objectFit={objectFit ?? 'cover'}
			src={src}
			{...backgroundProps}
		>
			{backgroundProps.children}
		</ImageBackground>
	);
};

BackgroundView.Top = Top;
BackgroundView.LeftActions = LeftActions;
BackgroundView.RightActions = RightActions;
BackgroundView.Title = Title;
BackgroundView.MidContent = MidContent;

namespace S {
	export const BackgroundPlaceholder = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		height: 100%;
	`;
}

export { type MidContentProps } from './MidContent';
export { type ActionProps, type TitleProps, type TopContentProps } from './TopContent';
